/**
 * Profile Service — Firestore CRUD for the operator's user document (web).
 *
 * Mirrors desktop/src/renderer/src/profile-service.ts.
 * Schema covers identity, business profile, operator config + guardrails,
 * connected provider metadata, onboarding progress, preferences,
 * and a lightweight activity summary.
 */

import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore'
import { db } from './firebase.js'

// ---------------------------------------------------------------------------
// Defaults
// ---------------------------------------------------------------------------

function defaultOperatorConfig() {
  return {
    approvalMode: 'manual_review',
    defaultEnvironment: 'personal',
    guardrails: {
      maxBudgetIncreasePercent: 20,
      maxSpendDeltaPerAction: 500,
      requireApprovalAboveAmount: 100
    }
  }
}

function defaultOnboarding() {
  return {
    startedAt: null,
    completedAt: null,
    steps: {
      // eslint-disable-next-line camelcase
      business_profile: false,
      // eslint-disable-next-line camelcase
      meta_connected: false,
      // eslint-disable-next-line camelcase
      google_connected: false,
      // eslint-disable-next-line camelcase
      engine_verified: false
    }
  }
}

function defaultPreferences() {
  return {
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    displayCurrency: 'USD',
    notifications: { email: true, desktop: true },
    morningBriefEnabled: false,
    watchLoopsEnabled: false
  }
}

function defaultActivitySummary() {
  return {
    totalActionsApproved: 0,
    totalActionsExecuted: 0,
    lastActionAt: null,
    platformsActive: []
  }
}

// ---------------------------------------------------------------------------
// Upsert on login
// ---------------------------------------------------------------------------

/**
 * Called immediately after every successful auth event.
 * - First login: seeds the full document with safe defaults.
 * - Return visit: updates identity fields and lastLoginAt only.
 * @param {import('firebase/auth').User} user
 */
export async function upsertUserOnLogin(user: { uid: string, email?: string | null, displayName?: string | null, photoURL?: string | null, providerData: Array<{ providerId?: string }>, emailVerified: boolean }) {
  const ref = doc(db, 'users', user.uid)
  const snap = await getDoc(ref)

  const identity = {
    uid: user.uid,
    email: user.email ?? '',
    displayName: user.displayName ?? '',
    photoURL: user.photoURL ?? '',
    provider: user.providerData[0]?.providerId ?? 'password',
    emailVerified: user.emailVerified,
    lastLoginAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  }

  if (!snap.exists()) {
    await setDoc(ref, {
      ...identity,
      createdAt: serverTimestamp(),
      businessProfile: null,
      operatorConfig: defaultOperatorConfig(),
      connectedProviders: {},
      onboarding: defaultOnboarding(),
      preferences: defaultPreferences(),
      activitySummary: defaultActivitySummary()
    })
  } else {
    await setDoc(ref, identity, { merge: true })
  }
}

// ---------------------------------------------------------------------------
// CRUD
// ---------------------------------------------------------------------------

/**
 * @param {string} uid
 * @returns {Promise<object|null>}
 */
export async function getProfile(uid: string): Promise<Record<string, unknown> | null> {
  try {
    const snap = await getDoc(doc(db, 'users', uid))
    return snap.exists() ? snap.data() : null
  } catch {
    return null
  }
}

/**
 * @param {string} uid
 * @param {object} data
 */
export async function saveProfile(uid: string, data: Record<string, unknown>) {
  await setDoc(
    doc(db, 'users', uid),
    { ...data, uid, updatedAt: serverTimestamp() },
    { merge: true }
  )
}
